import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Shield, Mail, FileText, Upload, Loader2, CheckCircle, Clock, XCircle, AlertTriangle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = '/api';

const BUSINESS_ROLES = [
  { value: 'owner', label: 'Business Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'employee', label: 'Employee' },
  { value: 'authorized_rep', label: 'Authorized Representative' }
];

const ClaimListing = ({ listing }) => {
  const { user } = useAuth();
  const [claimStatus, setClaimStatus] = useState(null);
  const [isClaimable, setIsClaimable] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [method, setMethod] = useState(null); // 'email_code' | 'proof_document'
  const [businessRole, setBusinessRole] = useState('owner');
  const [message, setMessage] = useState('');
  // Email code state
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  // Proof docs state
  const [proofDocs, setProofDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileRef = useRef(null);
  // Phone call state
  const [contactPhone, setContactPhone] = useState('');
  const [bestTime, setBestTime] = useState('');
  const [phoneSource, setPhoneSource] = useState('');
  // Submit state
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && listing?.id) checkClaimStatus();
  }, [user, listing?.id]);

  const checkClaimStatus = async () => {
    try {
      const res = await axios.get(`${API}/listings/${listing.id}/claim-status`);
      setClaimStatus(res.data.claim);
      setIsClaimable(res.data.is_claimable);
      setIsOwner(res.data.is_owner);
    } catch {
      // Not logged in or error
    }
  };

  const handleSendCode = async () => {
    setSendingCode(true);
    try {
      const res = await axios.post(`${API}/listings/${listing.id}/claim/send-code`);
      setCodeSent(true);
      setMaskedEmail(res.data.masked_email);
      toast.success('Verification code sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (proofDocs.length >= 3) {
      toast.error('Maximum 3 documents allowed');
      return;
    }
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProofDocs(prev => [...prev, res.data.url]);
      toast.success('Document uploaded');
    } catch {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!method) return;
    if (method === 'phone_call' && contactPhone.trim().length < 8) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (method === 'email_code' && !emailCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }
    if (method === 'proof_document' && proofDocs.length === 0) {
      toast.error('Please upload at least one proof document');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/listings/${listing.id}/claim`, {
        verification_method: method,
        business_role: businessRole,
        message,
        proof_documents: proofDocs,
        email_code: emailCode.trim(),
        contact_phone: contactPhone.trim(),
        best_time_to_call: bestTime,
        public_phone_source: phoneSource
      });
      toast.success(res.data.message);
      setShowForm(false);
      checkClaimStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  // Don't show for non-bulk or non-business listings
  if (!listing?.is_bulk_upload || listing?.listing_type !== 'business') return null;
  // Don't show if user is already the owner
  if (isOwner) return null;

  // User not logged in
  if (!user) {
    return (
      <Card className="border-amber-200 bg-amber-50/50 mb-6" data-testid="claim-listing-login-prompt">
        <CardContent className="p-5 flex items-start gap-3">
          <Shield className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Is this your business?</p>
            <p className="text-xs text-amber-700 mt-0.5">Log in to claim this listing and manage it yourself — for free.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Already has a claim
  if (claimStatus) {
    const statusConfig = {
      pending: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Pending Review', desc: 'Your claim is being reviewed by our team.' },
      approved: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50 border-green-200', label: 'Approved', desc: 'Your claim has been approved! You now manage this listing.' },
      rejected: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200', label: 'Rejected', desc: claimStatus.admin_note || 'Your claim was not approved.' }
    };
    const cfg = statusConfig[claimStatus.status] || statusConfig.pending;
    const Icon = cfg.icon;

    return (
      <Card className={`${cfg.bg} mb-6`} data-testid="claim-listing-status">
        <CardContent className="p-5 flex items-start gap-3">
          <Icon className={`h-5 w-5 ${cfg.color} mt-0.5 shrink-0`} />
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-slate-900">Claim Status:</p>
              <Badge variant="outline" className={cfg.color}>{cfg.label}</Badge>
            </div>
            <p className="text-xs text-slate-600 mt-0.5">{cfg.desc}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Not claimable (already claimed by someone else)
  if (!isClaimable) return null;

  return (
    <Card className="border-blue-200 bg-blue-50/30 mb-6" data-testid="claim-listing-section">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-slate-900">Is this your business?</p>
              <p className="text-xs text-slate-600 mt-0.5">Claim this listing to manage your business details, respond to reviews, and more — completely free.</p>
            </div>
          </div>
          {!showForm && (
            <Button
              size="sm"
              onClick={() => setShowForm(true)}
              className="bg-[#0F2C3E] shrink-0"
              data-testid="claim-listing-btn"
            >
              Claim Listing
            </Button>
          )}
        </div>

        {showForm && (
          <div className="mt-5 pt-5 border-t border-blue-200 space-y-4" data-testid="claim-form">
            {/* Step 1: Choose verification method */}
            {!method && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-700">How would you like to verify your ownership?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => setMethod('phone_call')}
                    className="flex items-start gap-3 p-4 border-2 rounded-xl text-left transition-all hover:border-blue-400 hover:bg-blue-50/50 sm:col-span-2"
                    data-testid="method-phone-call"
                  >
                    <Phone className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900">Request a Verification Call</p>
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Recommended</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">Someone from Discover Essex will call you within 48 hours to verify. Your phone number must match a publicly listed number for this business.</p>
                    </div>
                  </button>
                  {listing.email && (
                    <button
                      onClick={() => setMethod('email_code')}
                      className="flex items-start gap-3 p-4 border-2 rounded-xl text-left transition-all hover:border-blue-400 hover:bg-blue-50/50"
                      data-testid="method-email-code"
                    >
                      <Mail className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Email Verification</p>
                        <p className="text-xs text-slate-500 mt-0.5">We'll send a code to the email listed on this business</p>
                      </div>
                    </button>
                  )}
                  <button
                    onClick={() => setMethod('proof_document')}
                    className="flex items-start gap-3 p-4 border-2 rounded-xl text-left transition-all hover:border-blue-400 hover:bg-blue-50/50"
                    data-testid="method-proof-document"
                  >
                    <FileText className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Upload Proof Documents</p>
                      <p className="text-xs text-slate-500 mt-0.5">Business registration, utility bill, or other proof of ownership</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Verification details */}
            {method && (
              <div className="space-y-4">
                {/* Back button */}
                <button onClick={() => { setMethod(null); setCodeSent(false); setEmailCode(''); setProofDocs([]); setContactPhone(''); setBestTime(''); setPhoneSource(''); }} className="text-xs text-blue-600 hover:underline" data-testid="claim-back-btn">
                  &larr; Choose different method
                </button>

                {/* Phone Call flow */}
                {method === 'phone_call' && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-800">
                        <strong>How it works:</strong> A member of the Discover Essex team will call the number you provide within 48 hours. The phone number must match one publicly associated with this business (e.g., on your website, Google Business Profile, or Yell).
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Your contact phone number *</Label>
                      <Input
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        placeholder="e.g. 07700 900123"
                        className="mt-1"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Best time to call</Label>
                      <select
                        value={bestTime}
                        onChange={(e) => setBestTime(e.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                        data-testid="best-time-select"
                      >
                        <option value="">No preference</option>
                        <option value="morning">Morning (9am - 12pm)</option>
                        <option value="afternoon">Afternoon (12pm - 5pm)</option>
                        <option value="evening">Evening (5pm - 8pm)</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Where can we verify this phone number publicly? *</Label>
                      <select
                        value={phoneSource}
                        onChange={(e) => setPhoneSource(e.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                        data-testid="phone-source-select"
                      >
                        <option value="">Select source...</option>
                        <option value="website">Business website</option>
                        <option value="google">Google Business Profile</option>
                        <option value="yell">Yell / Yellow Pages</option>
                        <option value="social_media">Social media page</option>
                        <option value="other">Other public directory</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Email Code flow */}
                {method === 'email_code' && (
                  <div className="space-y-3">
                    {!codeSent ? (
                      <div>
                        <p className="text-sm text-slate-600 mb-3">
                          We'll send a 6-digit code to the email address registered with this business listing.
                        </p>
                        <Button
                          onClick={handleSendCode}
                          disabled={sendingCode}
                          size="sm"
                          className="bg-[#0F2C3E]"
                          data-testid="send-code-btn"
                        >
                          {sendingCode ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Mail className="h-4 w-4 mr-1.5" />}
                          {sendingCode ? 'Sending...' : 'Send Verification Code'}
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-green-700 mb-3">
                          <CheckCircle className="h-4 w-4 inline mr-1" />
                          Code sent to <strong>{maskedEmail}</strong>
                        </p>
                        <Label className="text-xs text-slate-600">Enter 6-digit code</Label>
                        <Input
                          value={emailCode}
                          onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          maxLength={6}
                          className="max-w-[180px] text-center text-lg tracking-[6px] font-mono mt-1"
                          data-testid="email-code-input"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Proof Document flow */}
                {method === 'proof_document' && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600">
                      Upload documents that prove your connection to this business (e.g., business registration, utility bill, official letter).
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {proofDocs.map((url, i) => (
                        <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border bg-white">
                          <img src={url} alt={`Proof ${i + 1}`} className="w-full h-full object-cover" />
                          <button
                            onClick={() => setProofDocs(prev => prev.filter((_, idx) => idx !== i))}
                            className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                            data-testid={`remove-doc-${i}`}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                      {proofDocs.length < 3 && (
                        <button
                          onClick={() => fileRef.current?.click()}
                          disabled={uploadingDoc}
                          className="w-20 h-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors"
                          data-testid="upload-proof-btn"
                        >
                          {uploadingDoc ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                          <span className="text-[10px] mt-0.5">{uploadingDoc ? 'Uploading' : 'Add'}</span>
                        </button>
                      )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={handleDocUpload} className="hidden" data-testid="proof-file-input" />
                    <p className="text-[11px] text-slate-400">Max 3 documents. JPG, PNG, or PDF accepted.</p>
                  </div>
                )}

                {/* Shared fields */}
                <div>
                  <Label className="text-xs text-slate-600">Your role at this business</Label>
                  <select
                    value={businessRole}
                    onChange={(e) => setBusinessRole(e.target.value)}
                    className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    data-testid="business-role-select"
                  >
                    {BUSINESS_ROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Additional information (optional)</Label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us anything that helps verify your connection to this business..."
                    rows={3}
                    className="mt-1 text-sm"
                    data-testid="claim-message"
                  />
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-1">
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || (method === 'phone_call' && (contactPhone.trim().length < 8 || !phoneSource)) || (method === 'email_code' && (!codeSent || emailCode.length < 6)) || (method === 'proof_document' && proofDocs.length === 0)}
                    className="bg-[#0F2C3E]"
                    data-testid="submit-claim-btn"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Shield className="h-4 w-4 mr-1.5" />}
                    {submitting ? 'Submitting...' : 'Submit Claim'}
                  </Button>
                  <Button variant="outline" onClick={() => { setShowForm(false); setMethod(null); }} data-testid="cancel-claim-btn">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClaimListing;
